/**
 * Feature gate for Desk → 0DTE Condor.
 *
 * Flag name: `compass.0dte_condor`
 *
 * Hide the nav item (and the page body) when:
 *   - Vite env `VITE_COMPASS_0DTE_CONDOR` is 0/false/off/no, or
 *   - localStorage `compass.0dte_condor` is 0/false/off/no
 *
 * Default is ON (paper SPA). A refresh is required after toggling
 * localStorage. This page never writes Book / Screener / Tracker state.
 *
 * Payoff series visibility: `compass.0dte_condor.payoff_series`
 * JSON `{ put, call, ic }` — default Combined only.
 *
 * Book/chart split: `compass.0dte_condor.book_split_px`
 * Pixel height of the positions table. Absent means the default share.
 */

export const ZERO_DTE_CONDOR_FLAG = 'compass.0dte_condor'
export const ZERO_DTE_CONDOR_ENV = 'VITE_COMPASS_0DTE_CONDOR'
export const ZERO_DTE_PAYOFF_SERIES_KEY = 'compass.0dte_condor.payoff_series'
/** Pixel height of the positions/orders table above the payoff chart. */
export const ZERO_DTE_BOOK_SPLIT_KEY = 'compass.0dte_condor.book_split_px'
const MIN_BOOK_PX = 112
const MIN_CHART_PX = 180

export type PayoffSeriesKey = 'put' | 'call' | 'ic'

export type PayoffSeriesVisibility = Record<PayoffSeriesKey, boolean>

export const DEFAULT_PAYOFF_SERIES_VISIBILITY: PayoffSeriesVisibility = {
  put: false,
  call: false,
  ic: true,
}

function readViteFlag(): string {
  try {
    const env = (import.meta as ImportMeta & { env?: Record<string, string | boolean | undefined> }).env
    const raw = env?.[ZERO_DTE_CONDOR_ENV]
    return raw == null ? '' : String(raw)
  } catch {
    return ''
  }
}

function isOff(raw: string): boolean {
  return ['0', 'false', 'off', 'no'].includes(raw.trim().toLowerCase())
}

function isOn(raw: string): boolean {
  return ['1', 'true', 'on', 'yes'].includes(raw.trim().toLowerCase())
}

export function isZeroDteCondorEnabled(): boolean {
  const env = readViteFlag()
  if (isOff(env)) return false
  if (isOn(env)) return true
  if (typeof localStorage !== 'undefined') {
    try {
      const ls = localStorage.getItem(ZERO_DTE_CONDOR_FLAG) ?? ''
      if (isOff(ls)) return false
    } catch {
      /* private mode */
    }
  }
  return true
}

function asBool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback
}

/** Read payoff chart series toggles. Default: Combined iron condor only. */
export function readPayoffSeriesVisibility(): PayoffSeriesVisibility {
  const fallback = { ...DEFAULT_PAYOFF_SERIES_VISIBILITY }
  if (typeof localStorage === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(ZERO_DTE_PAYOFF_SERIES_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<PayoffSeriesVisibility>
    const next: PayoffSeriesVisibility = {
      put: asBool(parsed.put, fallback.put),
      call: asBool(parsed.call, fallback.call),
      ic: asBool(parsed.ic, fallback.ic),
    }
    // Never leave the chart empty — keep Combined if everything was cleared.
    if (!next.put && !next.call && !next.ic) next.ic = true
    return next
  } catch {
    return fallback
  }
}

/** Saved table height, or null for the default share of the column. */
export function readBookSplitPx(): number | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(ZERO_DTE_BOOK_SPLIT_KEY)
    if (!raw) return null
    const n = Number(raw)
    if (!Number.isFinite(n)) return null
    return Math.max(MIN_BOOK_PX, Math.round(n))
  } catch {
    return null
  }
}

export function writeBookSplitPx(px: number): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(ZERO_DTE_BOOK_SPLIT_KEY, String(Math.round(px)))
  } catch {
    /* private mode / quota */
  }
}

/** Keep the table usable and leave the payoff chart a floor of its own. */
export function clampBookSplitPx(px: number, columnPx: number): number {
  const max = Math.max(MIN_BOOK_PX, columnPx - MIN_CHART_PX)
  return Math.round(Math.min(max, Math.max(MIN_BOOK_PX, px)))
}

export function writePayoffSeriesVisibility(next: PayoffSeriesVisibility): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(ZERO_DTE_PAYOFF_SERIES_KEY, JSON.stringify(next))
  } catch {
    /* private mode / quota */
  }
}
